import { useCallback, useEffect, useState } from 'react';

import { IItemOptionModifier, IPluConfig } from '@rbi-ctg/menu';
import { IMenuTileItemOption } from 'components/menu-tile-grid/types';
import { useFeatureDepositPriceExclusionQuery } from 'generated/sanity-graphql';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { PLUPosVendors } from 'state/store/hooks/enums';
import { useStoreUtils } from 'state/store/hooks/use-store-utils';

import { usePosVendor } from './use-pos-vendor';

export const useExcludedDepositValue = (itemOptions: IMenuTileItemOption): number => {
  const { store } = useStoreContext();
  const { isDelivery, isTakeout } = useServiceModeContext();
  const { vendor } = usePosVendor();
  const { pos } = store;

  const { prices } = useStoreUtils({
    resetStore: () => {},
    store,
  });
  const [depositValue, setDepositValue] = useState<number>(0);

  const { data: depositExclusionData } = useFeatureDepositPriceExclusionQuery({
    variables: { id: 'depositPriceExclusion' },
  });

  const getPluData = useCallback(
    (itemOptionModifier: IItemOptionModifier) => {
      const pluConfigVendor =
        pos?.vendor && PLUPosVendors
          ? PLUPosVendors[pos?.vendor as keyof typeof PLUPosVendors]
          : '';
      const pluConfigs =
        pluConfigVendor && itemOptionModifier?.pluConfigs
          ? itemOptionModifier?.pluConfigs[
              pluConfigVendor as keyof typeof itemOptionModifier.pluConfigs
            ]
          : [];
      const vendorConfigs = itemOptionModifier?.modifierMultiplier?.vendorConfigs ?? {};
      const { integration } = store;
      const posIntegration = integration?.partnerGroup?.posIntegration;
      const serviceMode = isDelivery ? 'delivery' : isTakeout ? 'pickup' : '';
      let plu: string = '';

      if (serviceMode) {
        // checking for plu in pluConfigs
        if (pluConfigs?.length && posIntegration?._ref) {
          const pluConfig = (pluConfigs as IPluConfig[])?.find(
            (pluConfigData: IPluConfig) =>
              pluConfigData?.posIntegration?._id === posIntegration?._ref &&
              pluConfigData?.serviceMode === serviceMode
          );
          if (pluConfig) {
            if (
              pluConfig?.vendorConfig &&
              typeof (pluConfig.vendorConfig as any)?.constantPlu === 'string'
            ) {
              plu = (pluConfig.vendorConfig as any).constantPlu;
            }
          }
        } else if (!Array.isArray(vendorConfigs) && vendorConfigs && vendor) {
          // checking for plu in vendorConfigs
          const vendorConfig = vendorConfigs[vendor as keyof typeof vendorConfigs];
          if (
            vendorConfig &&
            'constantPlu' in vendorConfig &&
            typeof vendorConfig.constantPlu === 'string'
          ) {
            plu = vendorConfig.constantPlu;
          }
        }
      }
      return plu;
    },
    [pos, store, isDelivery, isTakeout, vendor]
  );

  useEffect(() => {
    if (!itemOptions?.length || !prices) {
      return;
    }

    const excludedIdsArray =
      depositExclusionData?.DepositPriceExclusion?.excludeDepositFromItemPriceCalculation ?? [];

    const excludedIdsMap = excludedIdsArray.reduce<Record<string, true>>((acc, item) => {
      if (item?._id) {
        acc[item._id] = true;
      }
      return acc;
    }, {});

    for (const itemOption of itemOptions) {
      for (const itemOptionModifier of itemOption.options ?? []) {
        if (itemOptionModifier && 'modifierMultiplier' in itemOptionModifier) {
          const optionKey = itemOptionModifier?.modifierMultiplier?._id?.toString();
          if (optionKey && excludedIdsMap[optionKey]) {
            const plu = getPluData(itemOptionModifier);
            setDepositValue(prices[plu] ?? 0);
            return;
          }
        }
      }
    }
  }, [itemOptions, prices, depositExclusionData, getPluData]);

  return depositValue;
};
